import '../modal/ui-modal-controller';
import { Digest, element } from '../../global/index';
import { createElement } from '../../global/ui-helpers';
import type { AttributeValue } from '../ui-element.type';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { ComponentState } from '../ui-element.const';
import styles from './ui-toggle-tab.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIToggleTab
 * @element ui-toggle-tab
 * @classdesc Represents a class for <code>ui-toggle-tab</code> element.
 * It behaves like a radio button under the hood and is meant to be used together in
 * group setting with the wrapper <code>ui-toggle-tabbar</code>.
 * @property {string} label {@attr label} Label of the element.
 * @property {string} value {@attr value} Value attribute of the element.
 * @property {string} [name] {@attr name} Optional name attribute of the element.
 * @property {boolean} [checked] {@attr checked} Checked attribute of the element.
 * @property {HTMLInputElement} inputElement - Shortcut to an input type radio element.
 * @property {HTMLSpanElement} labelElement - Shortcut to an input text label element.
 * @example
 * <ui-toggle-tabbar>
 *   <ui-toggle-tab label="Private" name="userProfile" value="private" checked></ui-toggle-tab>
 *   <ui-toggle-tab label="Business" name="userProfile" value="business"></ui-toggle-tab>
 * </ui-toggle-tabbar>
 */
@Component({
    tag: 'ui-toggle-tab',
    styles,
})
export class UIToggleTab extends UIElement {
    @Attribute(String) label!: string;
    @Attribute(String) value!: string;
    @Attribute(Boolean) checked: boolean = false;
    @Attribute(String) name: string = '';

    @Child('.ui-toggle-tab__input') inputElement!: HTMLInputElement;
    @Child('.ui-toggle-tab__label') labelElement!: HTMLLabelElement;

    private _id: string = '';

    private inputChangeHandler: EventListener = (): void => {
        this.handleInputChange();
    };

    static get observedAttributes(): Array<string> {
        return ['label', 'checked', 'name', 'value'];
    }

    /**
     * @private
     */
    private get uuid(): string {
        if (!this._id) {
            this._id = Digest.randomId();
        }
        return this._id;
    }

    /**
     * @private
     */
    private get radioGroupName(): string {
        if (this.name) {
            return this.name;
        }

        const parentTabbar = this.closest('ui-toggle-tabbar');

        if (!parentTabbar) {
            return this.uuid;
        }

        if (!parentTabbar.hasAttribute('data-radiogroup-name')) {
            parentTabbar.setAttribute(
                'data-radiogroup-name',
                Digest.randomId()
            );
        }

        return parentTabbar.getAttribute('data-radiogroup-name')!;
    }

    /**
     * @private
     */
    private get inputValue(): string {
        return this.value || this.label;
    }

    /**
     * @returns {Record<string, string>}
     * @private
     */
    private extractDataAttributes(): Record<string, string> {
        return [...this.attributes]
            .filter((attribute) => attribute.name.startsWith('data-'))
            .reduce<Record<string, string>>((attributes, attribute) => {
                this.removeAttribute(attribute.name);
                attributes[attribute.name] = attribute.value;
                return attributes;
            }, {});
    }

    /**
     * @returns {HTMLInputElement}
     * @private
     */
    private createInputElement(): HTMLInputElement {
        return createElement({
            tagName: 'input',
            classList: {
                'ui-toggle-tab__input': true,
            },
            attributes: {
                type: 'radio',
                id: this.uuid,
                name: this.radioGroupName,
                value: this.inputValue,
                ...this.extractDataAttributes(),
                checked: this.checked ? '' : false,
            },
        })!;
    }

    /**
     * Handles input change event and updates checked state
     * @private
     */
    private handleInputChange(): void {
        if (this.inputElement.checked) {
            this.setAttribute('checked', '');
        } else {
            this.removeAttribute('checked');
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _: AttributeValue,
        newValue: string | null
    ): void {
        // Issue in the tests, the element references are undefined and same in ibank
        if (this.state !== ComponentState.Hydrated) {
            return;
        }

        switch (name) {
            case 'label':
                this.labelElement.innerText = newValue ?? '';
                if (!this.hasAttribute('value')) {
                    this.inputElement.value = this.labelElement.innerText;
                }
                break;
            case 'checked':
                if (newValue === null) {
                    this.inputElement.removeAttribute('checked');
                    this.inputElement.checked = false;
                    break;
                }

                this.inputElement.setAttribute('checked', '');
                this.inputElement.checked = true;
                this.inputElement.dispatchEvent(
                    new Event('change', { bubbles: true })
                );
                break;
            case 'name':
                this.inputElement.name = this.radioGroupName;
                break;
            case 'value':
                this.inputElement.value = this.inputValue;
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.append(
            element`
                <label for="${this.uuid}">
                    ${this.createInputElement()}
                    <span class="ui-toggle-tab__label">
                        ${this.label}
                    </span>
                </label>`
        );
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        const dataAttributes = this.extractDataAttributes();
        for (const [key, value] of Object.entries(dataAttributes)) {
            this.inputElement.setAttribute(key, value);
        }

        this.inputElement.addEventListener('change', this.inputChangeHandler);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.inputElement.removeEventListener(
            'change',
            this.inputChangeHandler
        );
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.inputElement.addEventListener('change', this.inputChangeHandler);
    }
}
